'use client';

import * as React from 'react';

// A swipe down closes the sheet once it travels this far (px), or moves at least
// SWIPE_MIN_DISTANCE this fast (px/s), so a shaky tap doesn't close it.
const SWIPE_CLOSE_DISTANCE = 80;
const SWIPE_CLOSE_VELOCITY = 500;
const SWIPE_MIN_DISTANCE = 16;

// Handle at the top of a phone bottom sheet. Dragging it pulls the sheet down with the finger.
function SwipeHandle({
  contentRef,
  onClose,
}: {
  contentRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}) {
  const dragRef = React.useRef<{ startY: number; startTime: number } | null>(null);

  const handleStart = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { startY: event.clientY, startTime: event.timeStamp };
    // The sheet's own enter transition would make it lag behind the finger.
    if (contentRef.current) contentRef.current.style.transition = 'none';
  };

  const handleMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || !contentRef.current) return;
    const offset = Math.max(0, event.clientY - dragRef.current.startY);
    contentRef.current.style.transform = `translateY(${offset}px)`;
  };

  const handleEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const content = contentRef.current;
    dragRef.current = null;
    if (!drag || !content) return;

    const offset = event.clientY - drag.startY;
    const velocity = (offset / Math.max(1, event.timeStamp - drag.startTime)) * 1000;
    if (
      offset > SWIPE_CLOSE_DISTANCE ||
      (offset > SWIPE_MIN_DISTANCE && velocity > SWIPE_CLOSE_VELOCITY)
    ) {
      // The exit animation starts from the dragged position.
      onClose();
      return;
    }

    content.style.transition = 'transform 200ms cubic-bezier(0.16, 1, 0.3, 1)';
    content.style.transform = '';
    content.addEventListener('transitionend', () => (content.style.transition = ''), {
      once: true,
    });
  };

  return (
    <div
      aria-hidden
      onPointerDown={handleStart}
      onPointerMove={handleMove}
      onPointerUp={handleEnd}
      onPointerCancel={handleEnd}
      className="sm:hidden absolute inset-x-0 top-0 flex h-6 touch-none items-center justify-center"
    >
      <div className="h-1 w-9 rounded-full bg-gray-60" />
    </div>
  );
}

export { SwipeHandle };
